export const readTokenClaims = (token) => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(decodeURIComponent(Array.from(atob(payload), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')))
  } catch {
    return {}
  }
}

export const membershipRoles = (membership) => [membership.role, ...(Array.isArray(membership.roles) ? membership.roles : [])].filter(Boolean)
const owns = (user, membership) => Boolean(user.userId && (membership.business?.ownerUserId || membership.ownerUserId) === user.userId)
export const isTenantUuid = (value) => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
export const sameTenant = (left, right) => isTenantUuid(left) && isTenantUuid(right) && left.toLowerCase() === right.toLowerCase()

export function tokenTenantContext(token, profileClaims = null) {
  // /users/me is authoritative when it supplies claims, including an empty object.
  // Decode Firebase only for compatibility with responses that omit claims.
  const claims = profileClaims ?? readTokenClaims(token || '')
  // All three formats are supported; a valid alternative must not be discarded.
  const candidates = [claims.tenantId, claims.tenant_id, ...(Array.isArray(claims.tenants) ? claims.tenants : [])]
  const tenantIds = [...new Set(candidates.filter(isTenantUuid).map((id) => id.toLowerCase()))].sort()
  const hasClaim = [claims.tenantId, claims.tenant_id, claims.tenants].some((value) => (
    value != null && value !== '' && !(Array.isArray(value) && value.length === 0)
  ))
  return { tenantIds, issue: tenantIds.length ? null : hasClaim ? 'invalid' : 'missing' }
}

// Memberships and the authenticated profile drive navigation; the API enforces authorization.
// Never infer a merchant from array order or the platform's tenant selector.
export function resolveMembershipTenant(user = {}) {
  const memberships = Array.isArray(user.memberships) ? user.memberships : []
  if (user.expectedTenantId || user.tenantId) return user.expectedTenantId || user.tenantId
  const { tenantIds } = tokenTenantContext(user.token, user.profileClaims)
  const merchantTenants = [...new Set(memberships.filter((m) => (
    membershipRoles(m).some((role) => role.startsWith('MERCHANT_')) || owns(user, m)
  )).map((m) => m.tenantId).filter(Boolean))]
  const candidates = merchantTenants.length ? merchantTenants : [...new Set(memberships.map((m) => m.tenantId).filter(Boolean))]
  const matching = candidates.filter((id) => tenantIds.some((claimed) => sameTenant(claimed, id)))
  if (matching.length === 1) return matching[0]
  return candidates.length === 1 ? candidates[0] : null
}

export function getFirstLoginAccess(user = {}) {
  const tenantId = resolveMembershipTenant(user)
  const tokenContext = tokenTenantContext(user.token, user.profileClaims)
  const memberships = Array.isArray(user.memberships) ? user.memberships : []
  const current = tenantId ? memberships.filter((m) => sameTenant(m.tenantId, tenantId)) : []
  const canConfigure = (m) => membershipRoles(m).includes('MERCHANT_ADMIN') || owns(user, m)
  const authorized = current.find(canConfigure)
  const ambiguousPending = !tenantId && memberships.some((m) => (
    m.tenantId && canConfigure(m) && m.firstLoginCompleted === false
  ))
  const pending = Boolean(authorized && authorized.firstLoginCompleted === false) || ambiguousPending
  const tokenReady = !tokenContext.issue && tokenContext.tenantIds.some((id) => sameTenant(id, tenantId))
  const claimIssue = !tenantId ? 'ambiguous' : tokenContext.issue || (tokenReady ? null : 'mismatch')
  const blockedReason = pending ? (user.status !== 'ACTIVE' ? 'inactive' : claimIssue) : null
  return {
    tenantId,
    canConfigure: Boolean(authorized),
    pending,
    tokenReady,
    blockedReason,
    needsWizard: pending && !blockedReason,
    missingTenant: Boolean(pending && claimIssue),
  }
}
